import {AutocompleteInput} from "@/components/input/Autocomplete";
import {useSearchUsersQuery} from "@/graphql/graphql";
import {useDebounceValue} from "@/hooks/use-debounce-value";
import {useState} from "react";

export interface User {
  id: string;
  name: string;
  email: string;
  organizationId: string;
}

interface UserAutocompleteProps {
  value: User | null;
  onChange: (value: User | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function UserAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "ユーザーを検索"
}: UserAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const debouncedSearchText = useDebounceValue(inputValue, 300); // 300ms遅延

  const {data: userData, isLoading} = useSearchUsersQuery(
    {searchText: debouncedSearchText},
    {
      enabled: debouncedSearchText.length >= 2,
      staleTime: 30000, // 30秒間キャッシュを保持
    }
  );

  // 選択中のユーザーを含むオプションリストを作成
  const options = [
    ...(value && !userData?.searchUsers?.some(user => user.id === value.id) ? [value] : []),
    ...(userData?.searchUsers?.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      organizationId: user.organizationId
    })) ?? [])
  ];

  return (
    <AutocompleteInput<User, false>
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      loading={isLoading}
      options={options}
      getOptionLabel={(option: User) => `${option.name} (${option.email})`}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      fetchOptions={async (words: string) => {
        setInputValue(words);
        return Promise.resolve();
      }}
      minWidth="100%"
      minWordLength={2}
      multiple={false}
      filterOptions={(options) => options} // APIで検索を行うため、クライアント側のフィルタリングは無効化
    />
  );
}
